/**
 * Section heading depth, inherited down the tree.
 *
 * `SectionCard` renders its title as a heading so a page built out of cards has
 * a heading outline at all. The right LEVEL, though, depends on where the card
 * sits: a card directly on a page belongs at `h2` under the page's `h1`, while
 * a card inside a collapsible group that already owns an `h2` belongs at `h3`.
 *
 * A card cannot know which it is, and threading a `heading-level` prop through
 * every intermediate component (a members panel, an activity panel, a history
 * panel) means every one of them has to remember to forward it. So the depth is
 * provided by whatever owns the group and inherited by the cards inside it.
 *
 * This is the ONLY way to set it. A per-card prop shipped alongside it briefly
 * and was removed with no caller ever passing one: it duplicated a mechanism
 * that already covers every case, and asked 60-odd call sites to know a depth
 * none of them can see. A card that genuinely needs a different level belongs
 * under a component that provides one.
 */

import { inject, provide, type InjectionKey } from 'vue';

export type SectionHeadingLevel = 2 | 3 | 4;

const SECTION_HEADING_LEVEL: InjectionKey<SectionHeadingLevel> = Symbol(
  'section-heading-level',
);

/** Declare the depth for every section card rendered below this component. */
export function provideSectionHeadingLevel(level: SectionHeadingLevel): void {
  provide(SECTION_HEADING_LEVEL, level);
}

/** The depth a section card should render at. `2` outside any group. */
export function useSectionHeadingLevel(): SectionHeadingLevel {
  return inject(SECTION_HEADING_LEVEL, 2);
}

/**
 * Is this subtree inside a `SectionPanel` (the detail-page collapsible)?
 *
 * A panel already renders the section's icon, title and description in its own
 * row, so a `SectionCard` below it would title the same section a second time -
 * the user reads "Documents / Files and paperwork kept here", expands, and meets
 * "Documents" again with its own icon. That shipped on eleven components at
 * once, because the nesting is CROSS-component: the page renders the panel, and
 * a child three files away renders the card.
 *
 * Provided rather than passed as a prop for the same reason the heading level
 * is: the components in between (a documents section, an inventory panel) would
 * all have to remember to forward it, and the one that forgets is invisible.
 */
const SECTION_PANEL_HOST: InjectionKey<boolean> = Symbol('section-panel-host');

/** Declared by SectionPanel for everything it renders. */
export function provideSectionPanelHost(): void {
  provide(SECTION_PANEL_HOST, true);
}

/** True inside a SectionPanel body; false anywhere else (edit pages, dialogs). */
export function useIsSectionPanelHosted(): boolean {
  return inject(SECTION_PANEL_HOST, false);
}
